
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { pushToDataLayer } from '@/utils/dataLayerUtils';
import { useAmplitudeEvents } from '@/utils/amplitudeEvents';
import { getPropertyShortName } from '@/utils/propertyUtils';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyTitle: string;
  checkIn?: Date;
  checkOut?: Date;
  nights: number;
  totalPrice: number;
  guests?: string;
  property?: {
    rating: number;
    guests: number;
    reviews?: any[];
    images?: string[];
    host?: { name: string };
  };
  maxGuests?: number;
  price?: number;
}

const BookingModal = ({ 
  isOpen, 
  onClose, 
  propertyTitle, 
  checkIn, 
  checkOut, 
  nights, 
  totalPrice,
  guests = '1',
  property,
  maxGuests = 6,
  price = 0
}: BookingModalProps) => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { trackBookingRequested, trackBookingModalClosed } = useAmplitudeEvents();

  // Memoize form validation
  const isFormValid = fullName.trim().length > 0 && 
                     email.trim().length > 0 && 
                     phone.trim().length > 0 &&
                     email.includes('@');

  const formatPrice = (amount: number) => `$${amount.toLocaleString()} MXN`;

  const formatDate = (date: Date) => date.toLocaleDateString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  const formatDateForDb = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const formatGuests = (guestCount: string) => {
    const count = parseInt(guestCount);
    return count === 1 ? '1 huésped' : `${count} huéspedes`;
  };

  const handleClose = () => {
    // Track Booking Modal Closed event
    const propertyShortName = getPropertyShortName(propertyTitle);
    const totalReviews = property?.reviews ? property.reviews.length : 0;
    const totalPhotos = property?.images ? property.images.length : 0;
    const hostName = property?.host?.name || '';

    trackBookingModalClosed(
      checkIn,
      checkOut,
      guests,
      propertyTitle,
      propertyShortName,
      property?.rating || 0,
      property?.guests || maxGuests,
      totalPrice,
      totalReviews,
      totalPhotos,
      hostName
    );

    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isFormValid) {
      toast({
        description: "Por favor completa todos los campos correctamente",
        variant: "destructive",
      });
      return;
    }

    if (!checkIn || !checkOut) {
      toast({
        description: "Las fechas de entrada y salida son requeridas",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const bookingData = {
        propertyTitle,
        fullName: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        checkIn: formatDateForDb(checkIn),
        checkOut: formatDateForDb(checkOut),
        nights,
        totalPrice,
      };

      const { data, error } = await supabase.functions.invoke('send-booking-email', {
        body: bookingData
      });

      if (error) {
        throw new Error(error.message);
      }
      
      // Push event to dataLayer after successful submission
      pushToDataLayer({
        event: 'booking_requested'
      });

      // Track Booking Requested Amplitude event
      const propertyShortName = getPropertyShortName(propertyTitle);
      const totalReviews = property?.reviews ? property.reviews.length : 0;
      const totalPhotos = property?.images ? property.images.length : 0;
      const hostName = property?.host?.name || '';

      trackBookingRequested(
        checkIn,
        checkOut,
        guests,
        propertyTitle,
        propertyShortName,
        property?.rating || 0,
        property?.guests || maxGuests,
        totalPrice,
        totalReviews,
        totalPhotos,
        hostName
      );
      
      toast({
        description: "Solicitud enviada exitosamente. Te contactaremos pronto para confirmar tu reservación.",
      });
      
      // Limpiar form y cerrar modal
      setFullName('');
      setEmail('');
      setPhone('');
      onClose();

    } catch (error: any) {
      console.error('Error sending booking request:', error);
      toast({
        description: "Error al enviar la solicitud. Inténtalo nuevamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className={cn(
        "sm:max-w-md",
        isMobile ? 
          "fixed bottom-0 left-0 right-0 top-auto h-[85vh] max-w-none rounded-t-2xl rounded-b-none m-0 p-0 translate-x-0 translate-y-0 data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom duration-150" :
          "max-h-[90vh]"
      )}>
        {/* Mobile header */}
        {isMobile && (
          <div className="flex items-center justify-start p-4 pb-2 border-b border-gray-100">
            <div className="flex-1">
              <DialogTitle className="text-lg font-semibold text-left">Solicitud de Reservación</DialogTitle>
              <DialogDescription className="text-sm text-gray-600 text-left mt-1">
                Completa los datos para solicitar tu reservación
              </DialogDescription>
            </div>
          </div>
        )}

        {/* Desktop header */}
        {!isMobile && (
          <DialogHeader>
            <DialogTitle>Solicitud de Reservación</DialogTitle>
            <DialogDescription>
              Completa los datos para solicitar tu reservación
            </DialogDescription>
          </DialogHeader>
        )}
        
        <div className={cn("flex-1", isMobile ? "p-4 pt-2 overflow-y-auto" : "overflow-y-auto")}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="fullName">Nombre completo</Label>
              <Input
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Tu nombre completo"
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="email">Correo electrónico</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@correo.com"
                required
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="phone">Teléfono</Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Tu número de teléfono"
                required
                className="mt-1"
              />
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <h4 className="font-semibold text-gray-900">Resumen de la reservación</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p><span className="font-medium">Propiedad:</span> {propertyTitle}</p>
                {checkIn && checkOut && (
                  <>
                    <p><span className="font-medium">Fechas:</span> {formatDate(checkIn)} - {formatDate(checkOut)}</p>
                    <p><span className="font-medium">Huéspedes:</span> {formatGuests(guests)}</p>
                    <p><span className="font-medium">Noches:</span> {nights}</p>
                    <p><span className="font-medium">Total:</span> {formatPrice(totalPrice)}</p>
                  </>
                )}
              </div>
            </div>
            
            <div className={cn("flex space-x-2", isMobile && "pb-4")}>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting ? 'Enviando...' : 'Enviar solicitud'}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
